import React, { useEffect, useRef } from "react";
import { Observer } from "mobx-react";
import locale from "@bonliva-crm/locale";
import { useStore, useTenant } from "@hooks";
import { ErrorCard, Spinner, Body, WithTooltip } from "@components";
import { TimeReportStore } from "./TimeReportStore";
import { TimereportDayForm } from "./TimereportDayForm/TimereportDayForm";
import DropdownButton from "@common/DropdownButton/DropdownButton";
import { Download, OpenInNew } from "mdi-material-ui";
import {
  CustomerContactPersonFragment,
  TimeReportTemplateType,
  useSetSkipTimereportReminderMutation
} from "@graphql";
import { Button, makeStyles, Typography, Grid, IconButton, Box } from "@material-ui/core";
import { useNavigation } from "@navigation";
import { FreeTimereportSelector } from "./FreeTimereportSelector/FreeTimereportSelector";
import { TimeReportStatus } from "@bookings/BookingProfile/TimeReport/TimeReportStatus";
import Alert from "@material-ui/lab/Alert";
import { BookingKitForm } from "@bookings/BookingProfile/TimeReport/TRKit/booking-kit-form";
import { downloadFile1 } from "@api";
import CustomerContactCard from "@customers/CustomerContactPersons/CustomerContactCard";
import SwitchCommon from "@common/forms/common/Switch";
import { ChooseEmailDialog } from "@bookings/BookingProfile/TimeReport/choose-email-dialog";
import { Close } from "@material-ui/icons";
import { BookingProfileStore } from "@bookings/BookingProfile/BookingProfileStore";
import { usePollingSimple } from "@bonliva-crm/ui";
import { useBookingKitFormConsumer } from "@bookings/BookingProfile/TimeReport/TRKit/use-booking-kit-form-context";
import { QuickTimeReportButtons } from "./QuickTimeReportButtons";

export type Props = {
  bookingId: string;
  consultantId: string;
  consultantName: string;
  week: number;
  year: number;
  bookingProfileStore: BookingProfileStore;
  handleRunAutoCreate: () => Promise<boolean>;
  autoCreateBookingKitDisabled: boolean;
};

export const TimeReportProfile = (props: Props) => {
  const { tenant } = useTenant();
  const localStore = useStore(
    () =>
      new TimeReportStore(
        props.bookingId,
        props.consultantId,
        props.week,
        props.year,
        tenant
      )
  );

  const { store: bookingKitStore } = useBookingKitFormConsumer();

  const initialTries = 1;
  const tries = useRef(initialTries);
  const maxTries = 10;

  const { start: startPolling, stop: stopPolling } = usePollingSimple(
    () => {
      if (tries.current > maxTries) {
        stopPolling();
        tries.current = initialTries;
        return;
      }
      props.bookingProfileStore.fetch(true);
      tries.current += 1;
    },
    1000,
    false
  );

  const nav = useNavigation();
  const classes = useStyles({});
  useEffect(() => {
    return () => {
      localStore?.bookingInfoUnSubscribe();
    };
  }, []);

  const [setSkipTimereportReminder, { loading }] = useSetSkipTimereportReminderMutation();

  const handleSkipTimereportReminder = (value: boolean) => {
    setSkipTimereportReminder({
      variables: {
        bookingId: props.bookingId,
        value
      }
    }).then((res) => {
      if (res.data?.setSkipTimereportReminder) {
        localStore.skipReminder = res.data.setSkipTimereportReminder.value;
      }
    });
  };

  return (
    <Observer>
      {() => {
        const dirty = localStore.dayFormStores?.some((x) => x.form.dirty);
        nav.lockNavigation("timeReport_dayEditor", !!dirty);

        if (localStore.isLoading) {
          return (
            <div style={{ padding: 20 }}>
              <Spinner size={25} />
            </div>
          );
        }

        const renderSuggestions = (opt) => {
          const item = localStore.contactPersons.find((x) => x.Email === opt.value);
          return item ? (
            <div style={{ pointerEvents: "none" }}>
              <CustomerContactCard item={item as CustomerContactPersonFragment} />
            </div>
          ) : null;
        };

        const sendToCustomer = (
          <>
            <Typography variant="caption">
              {locale.SendToCustomer}
              <IconButton size={"small"}>
                <Download
                  onClick={() => downloadFile1(localStore.timeReportDownloadLink)}
                  fontSize="small"
                />
              </IconButton>
              <IconButton size={"small"}>
                <OpenInNew
                  onClick={() => window.open(localStore.timeReportViewLink, "_blank")}
                  fontSize="small"
                />
              </IconButton>
            </Typography>
            <div
              onClick={() =>
                localStore.isDisabledSend
                  ? undefined
                  : localStore.confirmUnsavedDays(localStore.toggleEmailDialog)
              }
            >
              <DropdownButton
                disabled={localStore.loadingTemplates || localStore.isDisabledSend}
                className={classes.rightAction}
                ArrowButtonProps={{
                  style: { padding: 0, minWidth: 30, boxShadow: "none" },
                  tabIndex: 0
                }}
                ButtonProps={{ size: "small" }}
                onSelect={(_, btn) => {
                  const type = btn.value as TimeReportTemplateType;

                  localStore.setType(type);
                  bookingKitStore.form.setValue("timeReportType", type);
                }}
                options={localStore.templateTypeOptions}
                selected={localStore.getSelectedTemplateTypeIndex(
                  localStore.type,
                  localStore.templateTypeOptions
                )}
                withPopper
                PopperProps={{
                  popperOptions: {
                    style: { zIndex: 4000 }
                  }
                }}
              />
            </div>
          </>
        );

        const approve = (
          <div className={classes.rightAction}>
            <Typography variant="caption" align={"center"}>
              {locale.MarkCustomerApproved}
            </Typography>
            <Button
              variant={"contained"}
              fullWidth
              color={"secondary"}
              onClick={() => {
                localStore.handleApproveStatus(() => {
                  startPolling();
                });
              }}
              disabled={localStore.isDisabledApprove}
            >
              {locale.Approve}
            </Button>
          </div>
        );

        const submit = (
          <div className={classes.rightAction}>
            <Typography variant="caption" align={"center"}>
              {locale.MarkSubmitted}
            </Typography>
            <Button
              variant={"contained"}
              fullWidth
              color={"secondary"}
              onClick={() => {
                localStore.handleSubmitStatus(() => {
                  startPolling();
                });
              }}
              disabled={localStore.isDisabledSubmit}
            >
              {locale.Submit}
            </Button>
          </div>
        );

        const sendHba = (
          <div className={classes.rightAction}>
            <Typography variant="caption" align={"center"}>
              {locale.SendToHBA}
            </Typography>
            <Button
              disabled={
                props.autoCreateBookingKitDisabled || localStore.isDeletedByConsultant
              }
              variant={"contained"}
              color={"primary"}
              fullWidth
              // disabled={!kitDialogStore.responsibleBA || localStore.isDisabledSendHBA}
              // startIcon={<PresentToAll />}
              onClick={() =>
                localStore.confirmUnsavedDays(() =>
                  bookingKitStore.handleOpen(undefined, localStore.type, {
                    tags: localStore.bookingKitSupportTags,
                    comment: localStore.bookingKitSupportComment,
                    onSaved: localStore.reload
                  })
                )
              }
            >
              {locale.Send}
            </Button>
          </div>
        );

        const runAutoCreateBookingKit = (
          <div className={classes.rightAction}>
            <Button
              disabled={
                props.autoCreateBookingKitDisabled || localStore.isDeletedByConsultant
              }
              variant={"contained"}
              color={"primary"}
              fullWidth
              onClick={() =>
                localStore.confirmUnsavedDays(() =>
                  props.handleRunAutoCreate().then((started) => {
                    if (started) {
                      localStore.reload();
                    }
                  })
                )
              }
            >
              {locale.RunAutoCreateBookingKit}
            </Button>
          </div>
        );

        const toggleSkipReminder = (
          <WithTooltip
            title={
              "Varje vecka skickas automatiska påminnelser till konsulter som inte skickat in föregående veckas tidrapport. Om en konsult har skickat in sin tidrapport på papper/via mail men den ännu inte har hanterats i systemet då kan man slå på denna switchen och då skickas ingen påminnelse till konsulten."
            }
          >
            <div className={classes.rightAction}>
              <SwitchCommon
                formControlProps={{
                  disabled: localStore.isDisabledSetSkipReminder || loading
                }}
                switchProps={{ color: "secondary" }}
                value={localStore?.skipReminder}
                onChange={(v) => handleSkipTimereportReminder(v)}
                label={
                  loading ? (
                    <div>
                      {locale.SkipTimeReportReminder} <Spinner size={16} />
                    </div>
                  ) : (
                    locale.SkipTimeReportReminder
                  )
                }
              />
            </div>
          </WithTooltip>
        );

        return (
          <div style={{ width: "100%", position: "relative" }}>
            <ChooseEmailDialog
              open={localStore.emailDialogOpen}
              loading={localStore.loadingEmail}
              onClose={localStore.toggleEmailDialog}
              onConsultant={() => localStore.sendToConsultant()}
              onCustomer={() => {
                localStore.toggleEmailDialog();
                localStore.handleEmail({
                  withAttachments: true,
                  withAddRecipient: true,
                  withRemoveRecipient: true,
                  renderSuggestions: (opt) => renderSuggestions(opt),
                  customAttachment: (
                    <div
                      className={classes.emailAttachment}
                      onClick={() => window.open(localStore.timeReportViewLink, "_blank")}
                    >
                      <Body>{locale.PreviewTimeReport}</Body>
                      <OpenInNew color={"primary"} />
                    </div>
                  )
                });
              }}
            />

            <BookingKitForm
              expenses={localStore.dayFormStores?.flatMap((x) => x.form.values.expenses)}
              templateTypeOptions={localStore.templateTypeOptions}
              getSelectedTemplateTypeIndex={localStore.getSelectedTemplateTypeIndex}
            />
            {!localStore.error && (
              <TimeReportStatus
                statusHistory={localStore.statusHistory}
                sendFailures={localStore.sendFailures}
                setToDraft={localStore.setToDraft}
                disabledSetToDraft={!localStore.canSetToDraft}
                isSendToConsultant={localStore.needSignatureTROkInApp}
              />
            )}

            {localStore.timereportNotFound ? (
              <div>
                <Typography>Not found</Typography>
              </div>
            ) : (
              <Grid
                container
                style={{
                  overflow: "visible",
                  position: "relative",
                  maxHeight: "80vh"
                }}
              >
                <Grid item xs={10}>
                  {localStore.error ? (
                    <div style={{ padding: 20 }}>
                      <ErrorCard
                        message={localStore.error}
                        title={locale.ErrorOccurred}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        paddingTop: 5,
                        background: "#f0f0f0"
                      }}
                    >
                      {localStore.bookingInfoFailed && (
                        <Alert
                          severity="warning"
                          className={classes.bookingInfoFailed}
                          action={
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => localStore.retryBookingInfo()}
                            >
                              {locale.Retry}
                            </Button>
                          }
                        >
                          {locale.TimeReportBookingInfoFailed}
                        </Alert>
                      )}

                      {localStore.isDraftOrBlank && !localStore.bookingInfoFailed && (
                        <Box
                          flex
                          justifyContent="space-between"
                          className={classes.actionButtonsWrap}
                        >
                          {localStore.isDraftOrBlank && (
                            <QuickTimeReportButtons
                              dayFormStores={localStore.dayFormStores}
                              canEdit={!localStore.isHiddenActionBar}
                            />
                          )}

                          <div className={classes.clearBtnWrap}>
                            <Button
                              variant={"outlined"}
                              size="small"
                              onClick={localStore.clearDaysTimereport}
                              endIcon={<Close />}
                            >
                              {locale.Clear}
                            </Button>
                          </div>
                        </Box>
                      )}

                      {localStore.dayFormStores?.map((store) => (
                        <TimereportDayForm
                          key={store.stringId}
                          store={store}
                          dayFormStores={localStore.dayFormStores}
                          canEdit={!localStore.isHiddenActionBar}
                          canEditExpenses={localStore.canEditExpenses}
                          canEditOnlyMiles={localStore.canEditOnlyMiles}
                          consultantName={props.consultantName}
                          isOtherMonthBreakSide={!store.isMonthBreakOwned}
                        />
                      ))}
                    </div>
                  )}
                </Grid>
                <Grid item xs={2}>
                  {localStore.showFreeTR && (
                    <FreeTimereportSelector
                      loading={localStore.loadingFreeTimereports}
                      items={localStore.freeTimereports.filter(
                        (x) => x.status !== "Canceled"
                      )}
                      onAttach={(item) =>
                        localStore.attachToTimereport(item.timereportId)
                      }
                    />
                  )}

                  <div className={classes.rightActionWrap}>
                    {localStore.rejectedKits?.length > 0 && (
                      <div className={classes.rejectedKitsWrap}>
                        <Alert
                          severity="error"
                          style={{
                            marginBottom: 10,
                            padding: "0 5px",
                            borderRadius: "0",
                            width: "100%"
                          }}
                        >
                          {locale.KitForFix}
                        </Alert>

                        {localStore.rejectedKits?.map((x) => (
                          <div key={x._id} className={classes.rightAction}>
                            <Typography variant="caption" align={"center"}>
                              {locale.FixKit}
                            </Typography>
                            <Button
                              variant={"contained"}
                              color={"primary"}
                              fullWidth
                              key={x._id}
                              onClick={() =>
                                localStore.confirmUnsavedDays(() =>
                                  bookingKitStore.handleOpen(x, localStore.type, {
                                    onSaved: localStore.reload
                                  })
                                )
                              }
                            >
                              <Typography noWrap color={"inherit"}>
                                {`${x.name} ${locale.bookingKitType[x.type]}`}
                              </Typography>
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>{sendToCustomer}</div>
                    {submit}
                    {approve}
                    {sendHba}
                    {runAutoCreateBookingKit}
                    {localStore.isNationalProcurement && toggleSkipReminder}
                  </div>
                </Grid>
              </Grid>
            )}
          </div>
        );
      }}
    </Observer>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: "0px"
  },

  rightActionWrap: {
    top: 10,
    position: "sticky",
    flexDirection: "column",
    justifyContent: "space-evenly",
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  emailAttachment: {
    display: "flex",
    alignItems: "center",
    padding: " 16px 0",
    cursor: "pointer"
  },

  rejectedKitsWrap: {
    width: "100%",
    // textAlign: "center",
    marginBottom: 15,

    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center"
  },

  dayTableWrapper: {
    borderLeft: `1px solid ${theme.palette.divider}`
  },
  alertStandardInfo: {
    color: "rgb(13, 60, 97)",
    backgroundColor: "rgb(232, 244, 253)"
  },

  rightAction: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "135px",
    minWidth: "135px"
  },
  clearBtnWrap: {
    marginLeft: "auto"
  },
  bookingInfoFailed: {
    borderRadius: 0
  },

  actionButtonsWrap: {
    padding: `0 ${theme.spacing(1)}px`,
    display: "flex",
    justifyContent: "space-between"
  }
}));
